import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import { ArrowLeft, ChevronLeft } from 'lucide-react';
import { useRef } from 'react';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { appHomeOf } from '@/lib/app-home';
import { libraryPageSlot } from '@/lib/desktop-shell/library-page-slots';
import { useLibraryPage } from '@/lib/navigation';
import { settingsScreenTitle } from '@/lib/settings-page/sections';
import { useAppPath } from '@/vite/navigation-store';
import { useSwipeBack } from './use-swipe-back';

const TITLES: [prefix: string, title: string][] = [
  ['/settings', 'Settings'],
  ['/artifacts', 'Library'],
];

/**
 * A library page (Settings, Library) pushed as a screen of the phone app. The page portals in
 * as it does beside the desktop sidebar.
 */
export function MobileLibraryPage() {
  const library = useLibraryPage();
  const appPath = useAppPath();
  const screenRef = useRef<HTMLDivElement>(null);
  // The page was pushed (a history entry): back pops it, like the phone's back. Opened first
  // (a link), it leaves for the conversations.
  const back = () => {
    if (window.history.length > 1) window.history.back();
    else library.leave();
  };
  useSwipeBack(screenRef, back, library.open, { fromAnywhere: true });
  const workbench = useWorkbench();
  // An app's page is titled by its app (its own title hides on a phone).
  const appHome = appHomeOf(appPath);
  // A Settings sub-page is titled by its section.
  const title =
    settingsScreenTitle(appPath) ??
    (appHome === null ? undefined : (workbench.pluginDisplayNames[appHome.plugin] ?? 'App')) ??
    TITLES.find(([prefix]) => library.pathname.startsWith(prefix))?.[1] ??
    '';
  const settings = library.pathname === '/settings';

  return (
    <div
      aria-hidden={!library.open || undefined}
      className={cn(
        'absolute inset-0 z-30 flex flex-col bg-background shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.2,0.75,0.3,1)]',
        library.open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
      )}
      inert={!library.open || undefined}
      ref={screenRef}
    >
      <header
        className={cn(
          'flex shrink-0 items-center gap-1 px-1',
          settings ? 'h-16 bg-background pt-2' : 'h-12 border-hairline border-b bg-desktop-chrome',
        )}
      >
        <Button aria-label="Back" onClick={back} size="icon" variant="ghost">
          {settings ? <ArrowLeft className="size-5" /> : <ChevronLeft className="size-6" />}
        </Button>
        {settings ? (
          // Settings and its pages: a large title on the left (mobile 13–16, 37–41).
          <h1 className="type-page-title min-w-0 flex-1 truncate pl-1 font-bold tracking-tight">
            {title}
          </h1>
        ) : (
          <p className="min-w-0 flex-1 truncate text-center font-semibold">{title}</p>
        )}
        {!settings && <span aria-hidden className="size-10 shrink-0" />}
      </header>
      {library.open && (
        <div className="relative flex min-h-0 flex-1 flex-col" ref={libraryPageSlot.set} />
      )}
    </div>
  );
}
